import {
  CustomMappingAssociatedValueType,
  CustomMappingOption,
  CustomMappingType,
  IFailedCustomMappingRecord,
  IGetRecordResponse,
  ISourceEntityTypes,
  ITranslationMap,
  type ICustomMapping,
  type ICustomMappingSourceOption,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  ChartedItemScopeResolver,
  ServiceProviderHandler,
  TreatmentConfiguration,
  toINamedDocuments,
} from '@principle-theorem/principle-core';
import {
  ALL_SERVICE_CODES,
  ChartableSurface,
  IChartableItem,
  IChartedSurface,
  type IServiceCode,
  type ITreatmentConfiguration,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  IBlobFilenamePair,
  WithPrimaryKey,
  XSLXImporterExporter,
  asyncForEach,
  isINamedDocument,
  multiSortBy$,
  nameSorter,
  query$,
  snapshot,
  undeletedQuery,
  type INamedDocument,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, sortBy, uniqBy } from 'lodash';
import { Observable, of } from 'rxjs';
import { BaseCustomMappingHandler } from '../base-custom-mapping-handler';
import { CustomMapping } from '../custom-mapping';
import { type IBaseMigrationItemCode } from '../interfaces';
import { PracticeMigration } from '../practice-migrations';
import { BaseSourceEntity } from '../source/base-source-entity';
import { TranslationMapHandler } from '../translation-map';
import { ITEM_CODE_TO_SERVICE_CODE_MAPPINGS } from './ada-code-mappings';
import { ItemCodeResourceMapType, ItemCodesToXLSX } from './item-codes-to-xlsx';
import { XLSXToItemCodes } from './xlsx-to-item-codes';

export const ITEM_CODE_CUSTOM_MAPPING_TYPE = 'itemCodeMapping';

export const ITEM_CODE_MAPPING: ICustomMapping = CustomMapping.init({
  metadata: {
    label: 'Item Codes',
    description: `
      Define where non-ADA Item Codes from the source PMS should be mapped to in Principle. The different types and their effects are as follows:
        - Service Code: Map this to a valid Service Code in Principle.
        - Treatment: Map this service to a Treatment in Principle. eg. 'BOTOX' could be mapped to a 'Botox' Treatment.
        - Omit: This won't be migrated to a Treatment Plan, but will still be added to an Invoice if it has been billed for.
    `,
    type: ITEM_CODE_CUSTOM_MAPPING_TYPE,
  },
  type: CustomMappingType.SelectionList,
  labelOverrides: {
    sourceIdentifier: 'Id',
    sourceLabel: 'Non-ADA Code',
    destinationIdentifier: 'Map To',
    associatedValue: 'Service Code/Treatment',
  },
});

export abstract class ItemCodesMappingHandler<
  ADAItem extends ISourceEntityTypes,
  SourceEntity extends BaseSourceEntity<ADAItem>,
> extends BaseCustomMappingHandler<object, ItemCodeResourceMapType> {
  customMapping = ITEM_CODE_MAPPING;
  abstract translateFn: (record: ADAItem['data']) => IBaseMigrationItemCode;
  abstract sourceEntity: SourceEntity;

  async getSourceOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<ICustomMappingSourceOption[]> {
    const itemCodes = await this.getItemCodeOptions(migration);
    return itemCodes.map((record) => ({
      label: !record.description
        ? record.itemCode
        : `${record.itemCode} - ${record.description}`,
      value: record.id.toString(),
    }));
  }

  selectedOptionRequiresValue(
    destinationValue: ItemCodeResourceMapType
  ): boolean {
    return [
      ItemCodeResourceMapType.TreatmentConfiguration,
      ItemCodeResourceMapType.ServiceCode,
    ].includes(destinationValue);
  }

  async getSelectionListOptions(
    _migration: WithRef<IPracticeMigration>
  ): Promise<CustomMappingOption[]> {
    return snapshot(
      of([
        {
          value: ItemCodeResourceMapType.ServiceCode,
          description: 'Map this to a Service Code',
          hasAssociatedValue: true,
          associatedValueType: CustomMappingAssociatedValueType.String,
          associatedValueDescription:
            'Which Service Code should be used for this?',
        },
        {
          value: ItemCodeResourceMapType.TreatmentConfiguration,
          description: 'Map this to a Treatment',
          hasAssociatedValue: true,
          associatedValueType: CustomMappingAssociatedValueType.NamedDocument,
          associatedValueDescription:
            'Which Treatment should be used in place of this?',
        },
        {
          value: ItemCodeResourceMapType.Omit,
          description: 'Omit this Item Code from the migration',
          hasAssociatedValue: false,
        },
      ])
    );
  }

  getAssociatedValueOptions$(
    migration: IPracticeMigration,
    destinationValue: { value: ItemCodeResourceMapType }
  ): Observable<
    ({ name: string } | INamedDocument<ITreatmentConfiguration>)[]
  > {
    if (destinationValue.value === ItemCodeResourceMapType.ServiceCode) {
      return of(
        sortBy(
          ALL_SERVICE_CODES.map((code) => ({ name: String(code.code) })),
          'name'
        )
      );
    }

    if (
      destinationValue.value === ItemCodeResourceMapType.TreatmentConfiguration
    ) {
      return TreatmentConfiguration.all$(migration.configuration.brand).pipe(
        toINamedDocuments(),
        multiSortBy$(nameSorter())
      );
    }

    return of([]);
  }

  async getMappingBlob(
    migration: WithRef<IPracticeMigration>
  ): Promise<IBlobFilenamePair> {
    const { fileName, itemCodes, translator } =
      await this._getExporterData(migration);

    return new XSLXImporterExporter().getBlob(fileName, itemCodes, translator);
  }

  async downloadMapping(migration: WithRef<IPracticeMigration>): Promise<void> {
    const { fileName, itemCodes, translator } =
      await this._getExporterData(migration);

    await new XSLXImporterExporter().download(fileName, itemCodes, translator);
  }

  async uploadMapping(
    migration: WithRef<IPracticeMigration>,
    file: File
  ): Promise<void> {
    const items = await new XSLXImporterExporter().parse(
      file,
      new XLSXToItemCodes()
    );
    const translationMap = new TranslationMapHandler(migration);
    const records = await this.getRecords(translationMap);
    await asyncForEach(records, (record) =>
      translationMap.deleteRecord(record)
    );

    const sourceOptions = await this.getSourceOptions(migration);
    const treatmentConfigurations =
      await this._getTreatmentConfigurations(migration);

    const failedRecords: IFailedCustomMappingRecord[] = [];

    const recordsForUpload = items.map((item) => {
      const label = item.description
        ? `${item.code} - ${item.description}`
        : item.code;

      const value = sourceOptions.find(
        (sourceOption) => sourceOption.label === label
      )?.value;

      if (!value || !item.associatedValue) {
        return;
      }

      const treatmentConfiguration = treatmentConfigurations.find(
        (config) =>
          config.name.trim().toLowerCase().replace(/,/g, '') ===
          item.associatedValue?.toLowerCase().trim()
      );
      if (treatmentConfiguration) {
        return {
          associatedValue: treatmentConfiguration,
          destinationValue: {
            value: ItemCodeResourceMapType.TreatmentConfiguration,
          },
          sourceIdentifier: value,
          sourceLabel: label,
        };
      }

      const serviceCode = this.getServiceCode(item.associatedValue);
      if (serviceCode) {
        return {
          associatedValue: serviceCode.code.toString(),
          destinationValue: { value: ItemCodeResourceMapType.ServiceCode },
          sourceIdentifier: value,
          sourceLabel: label,
        };
      }

      failedRecords.push({
        sourceIdentifier: value,
        sourceLabel: label,
        reason: `Associated value not found: ${item.associatedValue}`,
      });
    });

    const result = await this.upsertBulk(
      compact(recordsForUpload),
      translationMap
    );

    const customMapping = await snapshot(
      PracticeMigration.findCustomMapping$(migration, this.customMapping)
    );
    if (!customMapping) {
      throw new Error('Custom mapping not found for updating failed records');
    }
    await Firestore.patchDoc(customMapping.ref, {
      failedRecords: [...failedRecords, ...result.failedRecords],
    });
  }

  getServiceCode(code?: string): IServiceCode | undefined {
    if (!code) {
      return;
    }
    return ServiceProviderHandler.findServiceCode(code);
  }

  async getItemCodeOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<IBaseMigrationItemCode[]> {
    const records = await this.sourceEntity
      .getAllRecords$(migration)
      .toPromise();
    const options = records
      .map((record) => record.data)
      .map(this.translateFn)
      .filter(
        (record) => !ServiceProviderHandler.findServiceCode(record.itemCode)
      );
    return sortBy(
      uniqBy(options, (option) => option.itemCode),
      'itemCode'
    );
  }

  async autocompleteMappings(
    migration: WithRef<IPracticeMigration>
  ): Promise<void> {
    const translationMap = new TranslationMapHandler(migration);
    const itemCodes = await this.getItemCodeOptions(migration);
    const records = await this.getRecords(translationMap);
    const mappedItemCodes = records
      .filter((record) => !!record.destinationIdentifier)
      .map((record) => record.sourceIdentifier);
    const unmappedItemCodes = itemCodes.filter(
      (option) => !mappedItemCodes.includes(option.id.toString())
    );

    const recordsForUpload = unmappedItemCodes.map((item) => {
      const formattedCode = item.itemCode
        .replace(/[.*+-]*/g, '')
        .replace(/[\sA-Za-z]*$/g, '');
      const codeMatch = ServiceProviderHandler.findServiceCode(formattedCode);

      return codeMatch
        ? {
            associatedValue: codeMatch.code.toString(),
            destinationValue: { value: ItemCodeResourceMapType.ServiceCode },
            sourceIdentifier: item.id.toString(),
            sourceLabel: item.itemCode,
          }
        : undefined;
    });

    const { failedRecords } = await this.upsertBulk(
      compact(recordsForUpload),
      translationMap
    );
    const customMapping = await snapshot(
      PracticeMigration.findCustomMapping$(migration, this.customMapping)
    );
    if (!customMapping) {
      throw new Error('Custom mapping not found for updating failed records');
    }
    await Firestore.patchDoc(customMapping.ref, {
      failedRecords,
    });
  }

  private async _getExporterData(
    migration: WithRef<IPracticeMigration>
  ): Promise<{
    fileName: string;
    itemCodes: IBaseMigrationItemCode[];
    translator: ItemCodesToXLSX;
  }> {
    const fileName = this.getFileName();
    const itemCodes = await this.getItemCodeOptions(migration);
    const translationMap = new TranslationMapHandler(migration);
    const translator = new ItemCodesToXLSX(
      await this.getRecords(translationMap),
      await this._getTreatmentConfigurations(migration),
      ITEM_CODE_TO_SERVICE_CODE_MAPPINGS
    );
    return { fileName, itemCodes, translator };
  }

  private async _getTreatmentConfigurations(
    migration: WithRef<IPracticeMigration>
  ): Promise<INamedDocument<ITreatmentConfiguration>[]> {
    return snapshot(
      query$(
        undeletedQuery(
          TreatmentConfiguration.col(migration.configuration.brand)
        )
      ).pipe(toINamedDocuments(), multiSortBy$(nameSorter()))
    );
  }
}

export function resolveMappedCode(
  itemCodeMappings: WithPrimaryKey<
    ITranslationMap<object, ItemCodeResourceMapType>
  >[],
  sourceIdentifier: string,
  serviceCode: string
): IServiceCode | undefined {
  const code = ServiceProviderHandler.findServiceCode(serviceCode);
  if (code) {
    return code;
  }

  const mappedCode = itemCodeMappings.find(
    (itemCode) =>
      itemCode.sourceIdentifier === sourceIdentifier &&
      itemCode.destinationValue?.value === ItemCodeResourceMapType.ServiceCode
  );
  if (!mappedCode?.associatedValue) {
    return;
  }

  if (isINamedDocument(mappedCode.associatedValue)) {
    return ServiceProviderHandler.findServiceCode(
      mappedCode.associatedValue.ref.id
    );
  }
  return ServiceProviderHandler.findServiceCode(mappedCode.associatedValue);
}

export async function resolveMappedCodeTreatment(
  sourceItemCodes: IGetRecordResponse<
    ISourceEntityTypes<IBaseMigrationItemCode>
  >[],
  _itemCodeMappings: WithPrimaryKey<
    ITranslationMap<object, ItemCodeResourceMapType>
  >[],
  treatmentConfigurationMappings: WithPrimaryKey<
    ITranslationMap<ITreatmentConfiguration>
  >[],
  defaultTreatmentConfiguration: WithRef<ITreatmentConfiguration>,
  _sourceIdentifier: string,
  serviceCode: IServiceCode
): Promise<WithRef<ITreatmentConfiguration> | undefined> {
  const sourceCode = sourceItemCodes.find(
    (code) => code.data.itemCode === serviceCode.code.toString()
  );
  if (!sourceCode) {
    return;
  }

  const mappedTreatment = treatmentConfigurationMappings.find(
    (mapping) => mapping.sourceIdentifier === sourceCode?.data.id
  );

  return mappedTreatment?.destinationIdentifier
    ? Firestore.getDoc(mappedTreatment.destinationIdentifier)
    : defaultTreatmentConfiguration;
}

export function resolveMappedCodeTreatmentPair(
  itemCodeMappings: WithPrimaryKey<
    ITranslationMap<object, ItemCodeResourceMapType>
  >[],
  treatmentMappings: WithPrimaryKey<ITranslationMap<ITreatmentConfiguration>>[],
  sourceIdentifier: string,
  serviceCode: string
): [
  IServiceCode | undefined,
  ITranslationMap<ITreatmentConfiguration> | undefined,
] {
  const code = resolveMappedCode(
    itemCodeMappings,
    sourceIdentifier,
    serviceCode
  );

  const mappedTreatment = treatmentMappings.find(
    (mapping) => mapping.sourceIdentifier === sourceIdentifier
  );
  return [code, mappedTreatment];
}

export async function resolveMappTreatmentConfiguration(
  defaultTreatmentConfiguration: WithRef<ITreatmentConfiguration>,
  mappedTreatment?: ITranslationMap<ITreatmentConfiguration>
): Promise<WithRef<ITreatmentConfiguration>> {
  return mappedTreatment?.destinationIdentifier
    ? Firestore.getDoc(mappedTreatment.destinationIdentifier)
    : defaultTreatmentConfiguration;
}

export function determineToothSurfaceByScope(
  chartedSurface: IChartedSurface,
  chartableItem: IChartableItem,
  scopeResolver: ChartedItemScopeResolver
): IChartedSurface {
  if (
    chartedSurface.chartedRef.tooth &&
    !chartedSurface.chartedRef.tooth.surface
  ) {
    const isOnlyAvailableOnArea = chartableItem.availableSurfaces.every(
      (surface) =>
        [
          ChartableSurface.WholeTooth,
          ChartableSurface.WholeMouth,
          ChartableSurface.Arch,
          ChartableSurface.Quadrant,
          ChartableSurface.MultipleTeeth,
        ].includes(surface)
    );
    if (isOnlyAvailableOnArea) {
      return chartedSurface;
    }

    const scope = scopeResolver.determineScope(chartableItem, [chartedSurface]);
    let surface: ChartableSurface | undefined;

    switch (scope) {
      case ChartableSurface.WholeTooth:
        surface =
          chartableItem.availableSurfaces.length === 1
            ? chartableItem.availableSurfaces[0]
            : ChartableSurface.Unknown;
        break;
      case ChartableSurface.Crown:
        surface = ChartableSurface.Crown;
        break;
      default:
        break;
    }

    return {
      ...chartedSurface,
      chartedRef: {
        ...chartedSurface.chartedRef,
        tooth: {
          ...chartedSurface.chartedRef.tooth,
          surface,
        },
      },
    };
  }
  return chartedSurface;
}
